import React, { useState, useRef, useEffect } from "react";
import { X, FileSpreadsheet, Download, AlertCircle } from "lucide-react";
import Button from "../ui/Button";
import { callObservationsAPI } from "../../services/api";

const MAX_CSV_SIZE_MB = 10;
const MAX_CSV_ROWS = 500;

const ALLOWED_ROLES = [
  "AI Assistant",
  "User",
  "Function Call",
  "Function Call Result",
];

// RFC-4180 cell escaping: quote the cell and double any inner quotes when it
// contains a comma, quote, or newline. Built programmatically (not hand-typed)
// so the JSON transcript cells — including function-call args — stay valid.
const csvEscape = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const toCsvLine = (cells) => cells.map(csvEscape).join(",");

const SAMPLE_HEADERS = [
  "call_identifier", "phone_number", "call_direction", "call_recording_url",
  "call_duration", "call_end_reason", "label", "transcript",
  "metadata.department", "metadata.ticket_id",
];

// A plain speech transcript (no audio recording).
const SAMPLE_TRANSCRIPT_SPEECH = JSON.stringify([
  { role: "AI Assistant", content: "Hi, how can I help?", start_time: 0.4, end_time: 2.6 },
  { role: "User", content: "A question about my bill.", start_time: 3.0, end_time: 5.1 },
]);

// A transcript that includes tool/function calls. `data.arguments` and
// `data.result` are themselves JSON-encoded strings.
const SAMPLE_TRANSCRIPT_FUNCTIONS = JSON.stringify([
  { role: "AI Assistant", content: "Hi, this is Acme support. How can I help?", start_time: 0.4, end_time: 2.6 },
  { role: "User", content: "What's the balance on my account?", start_time: 3.0, end_time: 5.1 },
  { role: "Function Call", content: "", start_time: 5.3, end_time: 5.3,
    data: { id: "call_9f2c", name: "getAccountBalance", arguments: JSON.stringify({ account_id: "AC-1023" }) } },
  { role: "Function Call Result", content: "", start_time: 6.0, end_time: 6.0,
    data: { id: "call_9f2c", name: "getAccountBalance", result: JSON.stringify({ balance: 42.5, currency: "USD" }) } },
  { role: "AI Assistant", content: "Your balance is $42.50. Anything else?", start_time: 6.4, end_time: 8.2 },
]);

const SAMPLE_CSV = [
  SAMPLE_HEADERS.join(","),
  toCsvLine(["CALL-001", "+14155551234", "inbound", "https://storage.example.com/recordings/call-001.wav", "245", "completed", "Support Call", "", "billing", "TKT-4821"]),
  toCsvLine(["CALL-002", "+14155555678", "outbound", "", "180", "customer-ended", "Follow-up", SAMPLE_TRANSCRIPT_SPEECH, "sales", "TKT-4822"]),
  toCsvLine(["CALL-003", "+14155559999", "inbound", "", "92", "completed", "Balance check", SAMPLE_TRANSCRIPT_FUNCTIONS, "support", "TKT-4823"]),
].join("\n");

const CSV_COLUMNS = [
  { name: "call_identifier", required: false, desc: "Unique ID for the call" },
  { name: "phone_number", required: false, desc: "Customer phone number" },
  { name: "call_direction", required: false, desc: "inbound or outbound" },
  { name: "call_recording_url", required: false, desc: "URL to the audio file. Required if no transcript." },
  { name: "transcript", required: false, desc: 'JSON array of turns (double-quoted). Required if no recording. e.g. [{"role":"User","content":"…"}]' },
  { name: "call_duration", required: false, desc: "Duration in seconds" },
  { name: "call_end_reason", required: false, desc: "e.g. completed, customer-ended" },
  { name: "label", required: false, desc: "Label / tag for grouping" },
  { name: "metadata.*", required: false, desc: "Custom fields prefixed with metadata." },
];

// RFC-4180-aware tokenizer: handles quoted fields containing commas, newlines,
// and escaped quotes ("") — required because the transcript cell holds JSON.
const tokenizeCsv = (text) => {
  const records = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } // escaped quote
        else { inQuotes = false; }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\n") {
      row.push(field); records.push(row); row = []; field = "";
    } else if (c !== "\r") {
      field += c;
    }
  }
  if (field !== "" || row.length > 0) { row.push(field); records.push(row); }
  // Drop fully-empty records (blank lines)
  return records.filter((r) => r.some((cell) => cell.trim() !== ""));
};

const CallUploadModal = ({ isOpen, onClose, clientId, agents = [], defaultAgentId = null, onSuccess }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [csvRows, setCsvRows] = useState([]);
  const [csvSkipped, setCsvSkipped] = useState([]);
  const [csvError, setCsvError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const csvInputRef = useRef(null);
  const [formData, setFormData] = useState({
    agent_id: defaultAgentId || "",
    label: "",
    call_direction: "",
  });

  useEffect(() => {
    if (isOpen && defaultAgentId) {
      setFormData((prev) => ({ ...prev, agent_id: defaultAgentId }));
    }
  }, [isOpen, defaultAgentId]);

  useEffect(() => {
    if (!isOpen) {
      setCsvFile(null);
      setCsvRows([]);
      setCsvSkipped([]);
      setCsvError(null);
      setError(null);
      setUploadResult(null);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  }, [isOpen]);

  // ─── CSV parsing ──────────────────────────────────────────────────────
  // Validates each row and formats the transcript cell into the role-based
  // turn structure. Rows that fail validation are skipped and reported.
  const parseCsv = (text) => {
    const records = tokenizeCsv(text);
    if (records.length < 2) return { error: "CSV must have a header row and at least one data row", rows: [], skipped: [] };

    const headers = records[0].map((h) => h.trim());
    if (!headers.includes("call_recording_url") && !headers.includes("transcript")) {
      return { error: "CSV must include a 'call_recording_url' or 'transcript' column", rows: [], skipped: [] };
    }

    const rows = [];
    const skipped = [];

    for (let i = 1; i < records.length; i++) {
      const values = records[i];
      const lineNo = i + 1; // 1-based, including the header line
      const row = {};
      const metadata = {};
      headers.forEach((h, idx) => {
        const val = (values[idx] ?? "").trim();
        if (h.startsWith("metadata.")) {
          const key = h.replace("metadata.", "");
          if (val) metadata[key] = val;
        } else {
          row[h] = val;
        }
      });
      if (Object.keys(metadata).length > 0) row.metadata = metadata;

      const flag = (reason) => skipped.push({ line: lineNo, id: row.call_identifier, reason });

      // ── Parse + validate the transcript cell (if any) ──────────────────
      let transcript;
      if (row.transcript) {
        let parsed;
        try {
          parsed = JSON.parse(row.transcript);
        } catch {
          flag("transcript is not valid JSON");
          continue;
        }
        if (!Array.isArray(parsed)) {
          flag("transcript must be a JSON array of turn objects");
          continue;
        }
        const badTurn = parsed.some(
          (t) => typeof t !== "object" || t === null || Array.isArray(t) || !ALLOWED_ROLES.includes(t.role),
        );
        if (parsed.length > 0 && badTurn) {
          flag(`each transcript turn must be an object with a role of ${ALLOWED_ROLES.join(", ")}`);
          continue;
        }
        transcript = parsed;
      }

      // ── At least one of recording_url / transcript is required ─────────
      const hasRecording = !!row.call_recording_url;
      const hasTranscript = Array.isArray(transcript) && transcript.length > 0;
      if (!hasRecording && !hasTranscript) {
        flag("row has neither call_recording_url nor a transcript");
        continue;
      }

      row.transcript = hasTranscript ? transcript : undefined;
      rows.push(row);
    }

    if (rows.length > MAX_CSV_ROWS) return { error: `CSV exceeds maximum of ${MAX_CSV_ROWS} rows`, rows: [], skipped };
    return { error: null, rows, skipped };
  };

  const handleCsvSelect = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;

    if (file.size > MAX_CSV_SIZE_MB * 1024 * 1024) {
      setCsvError(`File exceeds ${MAX_CSV_SIZE_MB}MB limit`);
      return;
    }
    if (!file.name.endsWith(".csv")) {
      setCsvError("Only .csv files are accepted");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const { error, rows, skipped } = parseCsv(event.target.result);
      setCsvSkipped(skipped || []);
      if (error) {
        setCsvError(error);
        setCsvRows([]);
      } else if (rows.length === 0) {
        setCsvError("No valid rows to import — every row was skipped (see details below).");
        setCsvRows([]);
        setCsvFile(file);
      } else {
        setCsvError(null);
        setCsvRows(rows);
        setCsvFile(file);
      }
    };
    reader.readAsText(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleCsvSelect({ target: { files: e.dataTransfer.files } });
  };

  // ─── Upload ────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (csvRows.length === 0) {
      setError("Please upload a valid CSV file");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      let created = 0;
      let failed = 0;
      for (const row of csvRows) {
        try {
          const callObservation = {
            call_recording_url: row.call_recording_url || undefined,
            call_identifier: row.call_identifier || undefined,
            call_direction: row.call_direction || formData.call_direction || undefined,
            call_duration: row.call_duration ? parseInt(row.call_duration) : undefined,
            call_end_reason: row.call_end_reason || undefined,
            label: row.label || formData.label || undefined,
            agent_id: formData.agent_id || undefined,
            source: "file_upload",
            observation_type: "voice",
            metadata: { ...row.metadata, phone_number: row.phone_number || undefined },
          };
          if (Array.isArray(row.transcript) && row.transcript.length > 0) {
            callObservation.call_transcript = row.transcript;
          }
          await callObservationsAPI.createCallObservation(clientId, {
            call_observation: callObservation,
          });
          created++;
        } catch {
          failed++;
        }
      }
      setUploadResult({ created, failed });
      if (created > 0) onSuccess?.({ count: created });
      if (failed === 0) setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(err.message || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_calls.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block align-bottom bg-card rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Import Calls</h3>
            <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-3">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {uploadResult && (
              <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-700">
                  {uploadResult.created} call{uploadResult.created !== 1 ? "s" : ""} imported successfully.
                  {uploadResult.failed > 0 && <span className="text-red-600 ml-1">{uploadResult.failed} failed.</span>}
                </p>
              </div>
            )}

            {/* Agent selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-1">Agent</label>
              <select
                value={formData.agent_id}
                onChange={(e) => setFormData({ ...formData, agent_id: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Select an agent</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            {/* CSV Format reference */}
            <div className="mb-4 bg-muted/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-foreground">CSV Format</p>
                <button onClick={handleDownloadSample} className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium">
                  <Download className="h-3.5 w-3.5" />
                  Download sample
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-1.5 pr-4 text-muted-foreground font-medium">Column</th>
                      <th className="text-left py-1.5 pr-4 text-muted-foreground font-medium">Required</th>
                      <th className="text-left py-1.5 text-muted-foreground font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CSV_COLUMNS.map((col) => (
                      <tr key={col.name} className="border-b border-border last:border-0">
                        <td className="py-1.5 pr-4 font-mono text-foreground">{col.name}</td>
                        <td className="py-1.5 pr-4">
                          {col.required ? (
                            <span className="text-red-500 font-medium">Yes</span>
                          ) : (
                            <span className="text-muted-foreground">No</span>
                          )}
                        </td>
                        <td className="py-1.5 text-muted-foreground">{col.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {csvError && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700">{csvError}</p>
              </div>
            )}

            {/* Drop zone */}
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="border-2 border-dashed border-border rounded-xl p-4 text-center hover:border-primary/40 transition-colors cursor-pointer"
              onClick={() => csvInputRef.current?.click()}
            >
              <FileSpreadsheet className="mx-auto h-10 w-10 text-gray-300 mb-3" />
              {csvFile ? (
                <div>
                  <p className="text-sm font-medium text-foreground">{csvFile.name}</p>
                  <p className="text-sm text-emerald-600 mt-1">{csvRows.length} row{csvRows.length !== 1 ? "s" : ""} ready to import</p>
                  <button
                    onClick={(e) => { e.stopPropagation(); setCsvFile(null); setCsvRows([]); setCsvSkipped([]); setCsvError(null); if (csvInputRef.current) csvInputRef.current.value = ""; }}
                    className="text-xs text-muted-foreground hover:text-muted-foreground mt-2"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mb-1">Drop a CSV file here, or click to browse</p>
                  <p className="text-xs text-muted-foreground">.csv only — max {MAX_CSV_SIZE_MB}MB, {MAX_CSV_ROWS} rows</p>
                </>
              )}
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvSelect}
                className="hidden"
              />
            </div>

            {/* Preview table */}
            {csvRows.length > 0 && (
              <div className="mt-4 overflow-x-auto max-h-40 overflow-y-auto border border-border rounded-lg">
                <table className="text-xs w-full">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-muted-foreground font-medium">#</th>
                      <th className="px-3 py-2 text-left text-muted-foreground font-medium">Identifier</th>
                      <th className="px-3 py-2 text-left text-muted-foreground font-medium">Recording URL</th>
                      <th className="px-3 py-2 text-left text-muted-foreground font-medium">Transcript</th>
                      <th className="px-3 py-2 text-left text-muted-foreground font-medium">Direction</th>
                      <th className="px-3 py-2 text-left text-muted-foreground font-medium">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {csvRows.slice(0, 10).map((row, i) => (
                      <tr key={i}>
                        <td className="px-3 py-1.5 text-muted-foreground">{i + 1}</td>
                        <td className="px-3 py-1.5 text-foreground font-mono">{row.call_identifier || "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[200px]">{row.call_recording_url || "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{row.transcript ? `${row.transcript.length} turn${row.transcript.length !== 1 ? "s" : ""}` : "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{row.call_direction || "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{row.call_duration ? `${row.call_duration}s` : "—"}</td>
                      </tr>
                    ))}
                    {csvRows.length > 10 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-1.5 text-muted-foreground text-center">
                          ... and {csvRows.length - 10} more rows
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Skipped rows */}
            {csvSkipped.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-amber-700">
                    {csvSkipped.length} row{csvSkipped.length !== 1 ? "s" : ""} skipped — not imported
                  </p>
                </div>
                <ul className="text-xs text-amber-700 space-y-1 max-h-32 overflow-y-auto pl-6 list-disc">
                  {csvSkipped.slice(0, 20).map((s, i) => (
                    <li key={i}>
                      Line {s.line}{s.id ? ` (${s.id})` : ""}: {s.reason}
                    </li>
                  ))}
                  {csvSkipped.length > 20 && <li>… and {csvSkipped.length - 20} more</li>}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-4 py-3 bg-muted/50 border-t border-border">
            <Button variant="secondary" onClick={onClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={uploading || csvRows.length === 0}
            >
              {uploading ? "Importing..." : `Import ${csvRows.length} Call${csvRows.length !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallUploadModal;
